import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, MessageSquare, Check } from 'lucide-react';
import { doc, getDoc, collection, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const ClientReply = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [conversation, setConversation] = useState(null);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    const loadConversation = async () => {
      if (!conversationId) {
        setError('Invalid link');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const conversationRef = doc(db, 'conversations', conversationId);
        const conversationDoc = await getDoc(conversationRef);

        if (!conversationDoc.exists()) {
          setError('Conversation not found');
          setLoading(false);
          return;
        }

        setConversation({ id: conversationDoc.id, ...conversationDoc.data() });
      } catch (err) {
        console.error('Error loading conversation:', err);
        setError('Failed to load conversation');
      } finally {
        setLoading(false);
      }
    };

    loadConversation();
  }, [conversationId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !conversation) return;

    setSending(true);
    try {
      // Add message to conversation
      const messagesRef = collection(db, 'conversations', conversationId, 'messages');
      await addDoc(messagesRef, {
        text: replyText,
        senderId: 'client', // Special ID for client responses via email
        senderName: 'Client',
        createdAt: serverTimestamp(),
        read: false,
      });

      // Update conversation last message
      const conversationRef = doc(db, 'conversations', conversationId);
      await updateDoc(conversationRef, {
        lastMessage: replyText.substring(0, 100),
        lastMessageAt: serverTimestamp(),
      });

      setSent(true);
      setReplyText('');
      
      // Show success for 3 seconds then reset
      setTimeout(() => {
        setSent(false);
      }, 3000);
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const getSitterName = () => {
    if (!conversation || !conversation.participantNames) return 'Your Pet Sitter';
    const sitterIds = conversation.participantIds?.filter(id => id !== 'client') || [];
    if (sitterIds.length > 0) {
      return conversation.participantNames[sitterIds[0]] || 'Your Pet Sitter';
    }
    return 'Your Pet Sitter';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pastel-pink/30 via-pastel-blue/20 to-pastel-purple/30 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pastel-purple mx-auto mb-4"></div>
          <p className="text-brand-brown font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pastel-pink/30 via-pastel-blue/20 to-pastel-purple/30 flex items-center justify-center p-4">
        <div className="card p-8 text-center max-w-md">
          <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">❌</span>
          </div>
          <h2 className="text-2xl font-bold text-brand-brown mb-2">Invalid Link</h2>
          <p className="text-brand-brown/60">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pastel-pink/30 via-pastel-blue/20 to-pastel-purple/30">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-brand-brown/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-3">
            <img src="/logo.png" alt="PetPages" className="h-12 w-12" />
            <div>
              <h1 className="text-2xl font-bold text-brand-brown">PetPages</h1>
              <p className="text-sm text-brand-brown/60">Reply to Your Pet Sitter</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {sent ? (
          <div className="card p-8 text-center">
            <div className="h-20 w-20 rounded-full bg-pastel-green flex items-center justify-center mx-auto mb-4">
              <Check className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-brand-brown mb-2">Message Sent!</h2>
            <p className="text-brand-brown/60 mb-6">
              Your reply has been sent to {getSitterName()}
            </p>
            <button
              onClick={() => setSent(false)}
              className="btn-primary mx-auto"
            >
              Send Another Message
            </button>
          </div>
        ) : (
          <div className="card p-8">
            <div className="text-center mb-8">
              <MessageSquare className="h-16 w-16 text-pastel-purple mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-brand-brown mb-2">
                Reply to {getSitterName()}
              </h2>
              <p className="text-brand-brown/60">
                Send a message directly from this link - no signup required!
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-brand-brown mb-2">
                  Your Message
                </label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your message here..."
                  rows="6"
                  required
                  className="textarea"
                  disabled={sending}
                />
                <p className="text-xs text-brand-brown/50 mt-1">
                  This message will be sent to {getSitterName()} via PetPages
                </p>
              </div>

              <button
                type="submit"
                disabled={!replyText.trim() || sending}
                className="w-full btn-primary py-4 text-lg flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-8 border-t border-brand-brown/10 text-center">
              <p className="text-sm text-brand-brown/60 mb-3">
                Want to manage all your pet care in one place?
              </p>
              <button
                onClick={() => navigate('/signup')}
                className="text-pastel-purple hover:text-pastel-blue font-medium text-sm"
              >
                Create a free PetPages account →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
        <p className="text-sm text-brand-brown/50">
          Powered by <span className="font-semibold">PetPages</span> 🐾
        </p>
      </div>
    </div>
  );
};

export default ClientReply;
